import React, { useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { SourceMapConsumer } from "source-map";
import JSZip from "jszip";
import StackTraceResolverModal from "./StackTraceResolverModal";

const StyledButton = styled.button`
    color: white;
    background-color: #003ce5;
    padding: 8px 12px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    margin: 0 10px;
    transition: background 0.15s;

    &:hover:not(:disabled) {
        background-color: #002bb3;
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

function extractVersionSmart(message) {
    const debugPath = message.match(/debug_v(\d+(\.\d+){0,2})/);
    if (debugPath) return "v" + debugPath[1];
    const stackMatch = message.match(/stackTrace:[\s\S]*?debug_v(\d+(\.\d+){0,2})/);
    if (stackMatch) return "v" + stackMatch[1];
    const generic = message.match(/\(v(\d+(\.\d+){0,2})\)/);
    if (generic) return "v" + generic[1];
    const loose = message.match(/v(\d+(\.\d+){0,2})/);
    if (loose) return "v" + loose[1];
    return null;
}
function extractStackTrace(message) {
    const match = message.match(/stackTrace: ([\s\S]*?)(?: - errorInfo:|$)/);
    return match ? match[1].trim() : null;
}
async function fetchSourceMapFromS3(version, cache) {
    if (cache[version]) return cache[version];
    const S3_BASE = "https://d2cqobm8wcb2vo.cloudfront.net/schemas/web_build";
    const url = `${S3_BASE}/debug_${version}.zip`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Source-map zip dosyası (${version}) bulunamadı!`);
    const blob = await res.blob();
    const zip = await JSZip.loadAsync(blob);
    let sourceMaps = {};
    zip.forEach((relPath, file) => {
        if (relPath.endsWith(".map")) {
            let key = relPath;
            if (key.startsWith("assets/")) key = key.slice(7);
            key = key.replace(/\.js\.map$/, ".js").replace(/\.map$/, "");
            sourceMaps[key] = file;
        }
    });
    cache[version] = sourceMaps;
    return sourceMaps;
}
async function resolveStackTraceWithSourceMap(stackTrace, sourceMaps) {
    const lines = stackTrace.split('\n');
    const resolvedLines = await Promise.all(
        lines.map(async (line) => {
            const match = line.match(/at (.+?\/)?([a-zA-Z0-9._-]+\.js):(\d+):(\d+)/);
            if (match) {
                const jsNameFull = match[2];
                const lineNum = parseInt(match[3], 10);
                const colNum = parseInt(match[4], 10);
                const mapFile = sourceMaps[jsNameFull];
                if (mapFile) {
                    try {
                        const mapJson = JSON.parse(await mapFile.async("string"));
                        const consumer = await new SourceMapConsumer(mapJson);
                        const pos = consumer.originalPositionFor({ line: lineNum, column: colNum });
                        consumer.destroy && consumer.destroy();
                        if (pos && pos.source) {
                            return `${line} → ${pos.source}:${pos.line}:${pos.column}${pos.name ? ` (${pos.name})` : ""}`;
                        }
                    } catch (e) {
                        return `${line}\n⚠️ MAP çözümleme hatası: ${e.message}`;
                    }
                } else {
                    return `${line}\n⚠️ MAP dosyası bulunamadı!`;
                }
            }
            return line;
        })
    );
    return resolvedLines.join('\n');
}

const StackTraceResolverButton = ({
                                      logs,
                                      disabled = false,
                                      buttonText = "Hepsini Çözümle",
                                  }) => {
    if (typeof window !== "undefined" && !window.__wasmMappingInitialized) {
        SourceMapConsumer.initialize({ "lib/mappings.wasm": "/mappings.wasm" });
        window.__wasmMappingInitialized = true;
    }
    const [modalOpen, setModalOpen] = useState(false);
    const [resolving, setResolving] = useState(false);
    const [results, setResults] = useState([]);
    const [error, setError] = useState("");

    const handleResolve = async () => {
        setModalOpen(true);
        setResults([]);
        setError("");
        setResolving(true);

        if (!logs || logs.length === 0) {
            setError("Çözümlenecek log yok!");
            setResolving(false);
            return;
        }
        const versionCache = {};
        let resultsArr = [];
        let hasError = false;

        for (let log of logs) {
            const version = extractVersionSmart(log.Message);
            const stackTrace = extractStackTrace(log.Message);
            if (!version || !stackTrace) {
                resultsArr.push({
                    Id: log.Id,
                    LogDate: log.LogDate,
                    Error: !version ? "Versiyon bulunamadı" : "Stack trace bulunamadı",
                    ResolvedStack: "",
                    Message: log.Message,
                    Version: version || "-",
                });
                hasError = true;
                continue;
            }
            try {
                const sourceMaps = await fetchSourceMapFromS3(version, versionCache);
                const resolved = await resolveStackTraceWithSourceMap(stackTrace, sourceMaps);
                resultsArr.push({
                    Id: log.Id,
                    LogDate: log.LogDate,
                    Error: "",
                    ResolvedStack: resolved,
                    Message: log.Message,
                    Version: version,
                });
            } catch (err) {
                resultsArr.push({
                    Id: log.Id,
                    LogDate: log.LogDate,
                    Error: err.message,
                    ResolvedStack: "",
                    Message: log.Message,
                    Version: version,
                });
                hasError = true;
            }
        }
        setResults(resultsArr);
        setResolving(false);
        if (hasError) toast.warn("Bazı loglar çözümlenemedi.");
        else toast.success("Tüm stack trace'ler çözümlendi!");
    };

    return (
        <>
            <StyledButton disabled={disabled} onClick={handleResolve}>
                {buttonText}
            </StyledButton>
            <StackTraceResolverModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                resolving={resolving}
                error={error}
                results={results}
            />
        </>
    );
};

export default StackTraceResolverButton;
